// Cards da Meta em Configurações > Conexões, um por produto (#320): helpers puros, sem React e sem rede.
// A regra de escopos pedidos por produto é do backend (`oauth.requested_scopes`); aqui só dizemos,
// para cada conexão, se ela tem o que o produto precisa.

export const PRODUCTS = ['ads', 'facebook', 'instagram', 'messaging']

export const PRODUCT_INFO = {
  ads: {
    label: 'Meta Ads',
    icon: 'meta',
    desc: 'Importe campanhas e métricas das contas de anúncios da Meta.',
    required: ['ads_read'],
    disconnect: 'Desconectar o Meta Ads? A coleta de métricas e a gestão de anúncios deixam de funcionar.',
  },
  facebook: {
    label: 'Facebook',
    icon: 'facebook',
    desc: 'Páginas do Facebook: Lead Ads e orgânico da Página.',
    required: ['pages_show_list', 'leads_retrieval'],
    disconnect: 'Desconectar o Facebook? Os leads de formulário e o orgânico da Página deixam de chegar.',
  },
  instagram: {
    label: 'Instagram',
    icon: 'instagram',
    desc: 'Orgânico do Instagram: posts, seguidores e comentários das contas ligadas às Páginas.',
    required: ['instagram_basic', 'instagram_manage_insights'],
    disconnect: 'Desconectar o Instagram? O orgânico do Instagram deixa de ser coletado.',
  },
  messaging: {
    label: 'Mensagens',
    icon: 'attend',
    desc: 'Instagram Direct e Messenger no Atendimento, na mesma caixa de entrada do WhatsApp.',
    required: ['pages_messaging', 'instagram_manage_messages'],
    disconnect: 'Desconectar as Mensagens? O Instagram Direct e o Messenger deixam de chegar e de ser respondidos pelo Atendimento.',
  },
}

export const productOf = (conn) => (PRODUCTS.includes(conn?.product) ? conn.product : 'ads')

export const scopesOf = (conn) => (conn?.scope || '').split(',').map((s) => s.trim()).filter(Boolean)

export const missingScopes = (conn) => {
  const granted = new Set(scopesOf(conn))
  return PRODUCT_INFO[productOf(conn)].required.filter((s) => !granted.has(s))
}

// Quem conectou: nome, senão e-mail, senão um texto neutro.
export const connectedBy = (conn) => conn?.meta_user_name || conn?.meta_user_email || 'Conta Meta'

const date = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : null)

// Status de uma conexão: ok, ou o motivo de precisar de atenção.
export function connectionStatus(conn) {
  if (!conn.is_active || conn.last_error) return { tone: 'amber', label: 'Reautorizar', reason: 'inactive' }
  const missing = missingScopes(conn)
  if (missing.length) return { tone: 'amber', label: 'Falta permissão', reason: 'scopes', missing }
  return { tone: 'green', label: 'Conectado', reason: 'ok' }
}

// Linha de apoio do card: quem conectou, quando, e até quando vale o token.
export function connectionSub(conn) {
  const parts = [connectedBy(conn)]
  const since = date(conn.created_at)
  if (since) parts.push(`desde ${since}`)
  const until = date(conn.expires_at)
  if (until) parts.push(`válido até ${until}`)
  return parts.join(' · ')
}

// Lista de conexões do servidor -> cards, na ordem Meta Ads, Facebook, Instagram, Mensagens.
// O Instagram avisa quando não há conexão do Facebook ativa: ele usa as Páginas vinculadas por lá.
export function buildMetaCards(conns = []) {
  const list = Array.isArray(conns) ? conns : []
  const activeFacebook = list.some((c) => productOf(c) === 'facebook' && c.is_active && !c.last_error)
  const cards = []
  for (const product of PRODUCTS) {
    for (const conn of list.filter((c) => productOf(c) === product)) {
      const status = connectionStatus(conn)
      const warnNoFacebook = product === 'instagram' && !activeFacebook
      cards.push({
        key: `meta-${product}-${conn.id}`,
        product,
        conn,
        title: PRODUCT_INFO[product].label,
        sub: connectionSub(conn),
        desc: warnNoFacebook
          ? 'Conecte o Facebook e vincule as Páginas: o Instagram lê as contas ligadas a elas.'
          : status.reason === 'scopes'
            ? `${PRODUCT_INFO[product].desc} Reautorize para conceder: ${status.missing.join(', ')}.`
            : PRODUCT_INFO[product].desc,
        status,
        needsReconnect: status.reason !== 'ok',
        warnNoFacebook,
      })
    }
  }
  return cards
}
